import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original.__retry && !String(original.url).includes("/auth/")) {
      original.__retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => (typeof x?.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}

export const apiFile = (p) => `${process.env.REACT_APP_BACKEND_URL}${p}`;

export function usePhotoUpload() {
  const { setUser } = useAuth();
  return async (file) => {
    const dataUrl = await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const side = Math.min(img.width, img.height);
          canvas.width = canvas.height = 256;
          canvas.getContext("2d").drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 256, 256);
          res(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = () => rej(new Error("img"));
        img.src = reader.result;
      };
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });
    const { data } = await api.post("/me/photo", { data_url: dataUrl });
    setUser((u) => ({ ...u, photo_url: data.photo_url }));
  };
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  return <AuthContext.Provider value={{ user, setUser, checking }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export function Protected({ role, children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Cargando…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/acceso" replace />;
  if (role) {
    const ok = Array.isArray(role) ? role.includes(user.role) : user.role === role;
    if (!ok) {
      return <Navigate to={user.role === "admin" ? "/gestion" : user.role === "teacher" ? "/profesora" : "/alumno"} replace />;
    }
  }
  return children;
}
